import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Text,
  Textarea,
} from '@shared/components';
import { useState } from 'react';

export type TwoFaActionVariant = 'reset' | 'unlock';

const VARIANT_CONFIG = {
  reset: {
    title: 'Reset 2FA',
    description: 'หลังจาก Reset ผู้ใช้งานจะต้องตั้งค่า Google Authenticator ใหม่อีกครั้ง',
    helperText: 'กรุณาระบุเหตุผล',
  },
  unlock: {
    title: 'Unlock 2FA',
    description: 'หลังจาก Unlock ผู้ใช้งานจะสามารถ Login ด้วย Google Authenticator ได้ตามปกติ',
    helperText: 'กรุณากรอกเหตุผล',
  },
} as const satisfies Record<
  TwoFaActionVariant,
  { title: string; description: string; helperText: string }
>;

interface TwoFaActionModalProps {
  variant: TwoFaActionVariant;
  open: boolean;
  onClose: () => void;
  onConfirm?: (reason: string) => Promise<void> | void;
}

export default function TwoFaActionModal(props: Readonly<TwoFaActionModalProps>) {
  const { variant, open: isOpen, onClose, onConfirm } = props;

  const [reason, setReason] = useState('');
  const [hasError, setHasError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { title, description, helperText } = VARIANT_CONFIG[variant];

  const handleClose = () => {
    if (isSubmitting) return;
    setReason('');
    setHasError(false);
    onClose();
  };

  const handleConfirm = async () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setHasError(true);
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm?.(trimmedReason);
      setReason('');
      setHasError(false);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle onClose={handleClose}>{title}</DialogTitle>
      <DialogContent dividers>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
          <Text>{description}</Text>
          <Textarea
            label='ระบุเหตุผล'
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (hasError) setHasError(false);
            }}
            placeholder='กรุณาระบุเหตุผล'
            required
            error={hasError}
            helperText={hasError ? helperText : undefined}
            disabled={isSubmitting}
            slotProps={{ htmlInput: { sx: { resize: 'vertical' } } }}
          />
        </Flex>
      </DialogContent>
      <DialogActions>
        <Button
          variant='outlined'
          color='secondary'
          size='large'
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={handleConfirm}
          loading={isSubmitting}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
